import { ref, set, remove, update, onValue, get } from 'firebase/database';
import { useState, useEffect } from 'react';
import { db } from '../firebase';

// ===========================
// owner/staff 用: イベントの schedule を購読・操作
// ===========================
export const useSchedule = (eventId) => {
  const [schedule, setSchedule] = useState({
    token: null, dates: {}, attendance: {},
    sessions: null, session_attendance: {}, cast_comments: {},
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!eventId) return;
    const schedRef = ref(db, `events/${eventId}/schedule`);
    const unsub = onValue(schedRef, (snap) => {
      const val = snap.val() || {};
      setSchedule({
        token:               val.token               || null,
        dates:               val.dates               || {},
        attendance:          val.attendance          || {},
        sessions:            val.sessions            ?? null,
        session_attendance:  val.session_attendance  || {},
        cast_comments:       val.cast_comments       || {},
      });
      setLoading(false);
    });
    return unsub;
  }, [eventId]);

  /** トークンが未生成なら UUID を発行して保存（events 配下のみ・外部コレクション不要） */
  const ensureToken = async () => {
    if (schedule.token) return schedule.token;
    const token = crypto.randomUUID();
    await set(ref(db, `events/${eventId}/schedule/token`), token);
    return token;
  };

  /** 単一日付の ON/OFF トグル (attendance は削除しない) */
  const toggleDate = async (dateStr) => {
    if (schedule.dates?.[dateStr]) {
      await remove(ref(db, `events/${eventId}/schedule/dates/${dateStr}`));
    } else {
      await set(ref(db, `events/${eventId}/schedule/dates/${dateStr}`), true);
    }
  };

  /** 日付配列を一括で開催日に追加 */
  const addDates = async (dateList) => {
    const updates = {};
    dateList.forEach(d => { updates[`events/${eventId}/schedule/dates/${d}`] = true; });
    if (Object.keys(updates).length > 0) await update(ref(db), updates);
  };

  /**
   * キャストの出欠を保存
   * @param {string} castId
   * @param {string} dateStr  YYYY-MM-DD
   * @param {'working'|'absent'|null} status  null で削除
   */
  const setAttendance = async (castId, dateStr, status) => {
    const attRef = ref(db, `events/${eventId}/schedule/attendance/${castId}/${dateStr}`);
    if (status === null) {
      await remove(attRef);
    } else {
      await set(attRef, status);
    }
  };

  /** 前月以前の attendance を削除 (ロード時に自動呼び出し) */
  const cleanupOldAttendance = async (attendanceData) => {
    const now = new Date();
    // 先月1日よりも前のデータを削除
    const cutoff = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const cutoffStr = `${cutoff.getFullYear()}-${String(cutoff.getMonth() + 1).padStart(2, '0')}-01`;

    const updates = {};
    Object.entries(attendanceData || {}).forEach(([castId, days]) => {
      Object.keys(days).forEach(dateStr => {
        if (dateStr < cutoffStr) {
          updates[`events/${eventId}/schedule/attendance/${castId}/${dateStr}`] = null;
        }
      });
    });
    if (Object.keys(updates).length > 0) await update(ref(db), updates);
  };

  /** 部数を設定（null で部制なし） */
  const setSessions = (count) => {
    const val = count === '' || count === null || Number(count) <= 1 ? null : Number(count);
    set(ref(db, `events/${eventId}/schedule/sessions`), val);
  };

  /** 部別出欠を保存 */
  const setSessionAttendance = async (castId, dateStr, session, status) => {
    const path = ref(db, `events/${eventId}/schedule/session_attendance/${castId}/${dateStr}/${session}`);
    if (status === null) { await remove(path); } else { await set(path, status); }
  };

  /** 日付の全部を一括設定 */
  const setAllSessionAttendance = async (castId, dateStr, sessionCount, status) => {
    const updates = {};
    for (let s = 1; s <= sessionCount; s++) {
      const p = `events/${eventId}/schedule/session_attendance/${castId}/${dateStr}/${s}`;
      updates[p] = status === null ? null : status;
    }
    await update(ref(db), updates);
  };

  /** キャストコメントを保存（空文字で削除） */
  const setCastComment = async (castId, dateStr, comment) => {
    const path = ref(db, `events/${eventId}/schedule/cast_comments/${castId}/${dateStr}`);
    if (!comment) { await remove(path); } else { await set(path, comment); }
  };

  return {
    schedule, loading,
    ensureToken, toggleDate, addDates, setAttendance, cleanupOldAttendance,
    setSessions, setSessionAttendance, setAllSessionAttendance, setCastComment,
  };
};

// ===========================
// キャスト向け公開ページ用: eventId + token でアクセス
// URL: /schedule/:eventId/:token
// ===========================
export const useScheduleByEventToken = (eventId, token) => {
  const [eventName, setEventName] = useState('');
  const [schedule, setSchedule] = useState({ dates: {}, attendance: {}, sessions: null, session_attendance: {}, cast_comments: {} });
  const [casts, setCasts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!eventId || !token) return;
    let schedUnsub, castsUnsub;

    // トークンを検証
    get(ref(db, `events/${eventId}/schedule/token`)).then(snap => {
      if (!snap.exists() || snap.val() !== token) {
        setError('invalid_token');
        setLoading(false);
        return;
      }

      get(ref(db, `events/${eventId}/event_name`)).then(s => {
        if (s.exists()) setEventName(s.val());
      });

      schedUnsub = onValue(ref(db, `events/${eventId}/schedule`), snap => {
        const val = snap.val() || {};
        setSchedule({
          dates: val.dates || {}, attendance: val.attendance || {},
          sessions: val.sessions ?? null,
          session_attendance: val.session_attendance || {},
          cast_comments: val.cast_comments || {},
        });
      });

      castsUnsub = onValue(ref(db, `events/${eventId}/casts`), snap => {
        setCasts(snap.val() || {});
        setLoading(false);
      });
    }).catch(() => {
      setError('load_failed');
      setLoading(false);
    });

    return () => {
      if (schedUnsub) schedUnsub();
      if (castsUnsub) castsUnsub();
    };
  }, [eventId, token]);

  const setAttendance = async (castId, dateStr, status) => {
    const attRef = ref(db, `events/${eventId}/schedule/attendance/${castId}/${dateStr}`);
    if (status === null) { await remove(attRef); } else { await set(attRef, status); }
  };

  const setSessionAttendance = async (castId, dateStr, session, status) => {
    const path = ref(db, `events/${eventId}/schedule/session_attendance/${castId}/${dateStr}/${session}`);
    if (status === null) { await remove(path); } else { await set(path, status); }
  };

  const setAllSessionAttendance = async (castId, dateStr, sessionCount, status) => {
    const updates = {};
    for (let s = 1; s <= sessionCount; s++) {
      updates[`events/${eventId}/schedule/session_attendance/${castId}/${dateStr}/${s}`] = status === null ? null : status;
    }
    await update(ref(db), updates);
  };

  const setCastComment = async (castId, dateStr, comment) => {
    const path = ref(db, `events/${eventId}/schedule/cast_comments/${castId}/${dateStr}`);
    if (!comment) { await remove(path); } else { await set(path, comment); }
  };

  return { eventName, schedule, casts, loading, error, setAttendance, setSessionAttendance, setAllSessionAttendance, setCastComment };
};

// ===========================
// ユーティリティ: 繰り返しルールから日付リストを生成
// ===========================
/**
 * @param {{ weekdays: number[], nth: number[]|null }} rule
 * @param {number} year
 * @param {number} month  0始まり
 * @returns {string[]} YYYY-MM-DD の配列
 */
export const generateRecurringDates = (rule, year, month) => {
  const { weekdays, nth } = rule;
  if (!weekdays || weekdays.length === 0) return [];

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const weekdayCount = {}; // weekday → 何回目か
  const result = [];

  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(year, month, day);
    const wd = date.getDay(); // 0=日
    if (!weekdays.includes(wd)) continue;

    weekdayCount[wd] = (weekdayCount[wd] || 0) + 1;
    const occurrence = weekdayCount[wd];

    if (!nth || nth.includes(occurrence)) {
      const mm = String(month + 1).padStart(2, '0');
      const dd = String(day).padStart(2, '0');
      result.push(`${year}-${mm}-${dd}`);
    }
  }
  return result;
};

/** 今日の YYYY-MM-DD を返す */
export const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** 次の開催日（当日含む）を返す。開催日未設定なら今日の日付を返す */
export const nextEventDate = (dates) => {
  const today = todayStr();
  const upcoming = Object.keys(dates || {}).filter(d => d >= today).sort();
  return upcoming.length > 0 ? upcoming[0] : today;
};
